import {plainToInstance} from 'class-transformer';

import {NamespaceSocket} from '../core/namespaceSocket';
import {ApiResponse} from '../entities/apiResponse';
import {ApiError} from '../entities/error';
import {Group} from '../entities/groups/group';
import {GroupContentItem} from '../entities/groups/groupContentItem';
import {GroupRequest} from '../entities/groups/groupRequest';
import {GroupsTag} from '../entities/groups/groupTag';
import {Pagination} from '../entities/pagination';
import {
  IGetGroupContentFilter, IGroupClientToServerEvents,
  IGroupFullData,
  IGroupRequestChangeResponse, IGroupServerToClientEvents,
} from '../interfaces/socket.interface';
import {Manager} from 'socket.io-client/build/esm/manager';

/**
 * Socket class, that works with Groups namespace
 *
 * @internal
 * */
export class GroupSocket extends NamespaceSocket<GroupRequest, IGroupClientToServerEvents, IGroupServerToClientEvents> {

  /**
   * Time, for how long we will wait for new changed, before send it
   *
   * @internal
   * */
  protected requestQueueTime = 50;

  /**
   * @ignore
   * @internal
   * */
  constructor(manager: Manager, success: () => void, errorCallback: (err: ApiError) => void) {
    super(manager, 'group', success, errorCallback);
  }

  /**
   * Here we will get all changes for all groups
   *
   * @param callback that will be triggered, when event will be called, array of {@link IGroupRequestChangeResponse} will be sent
   * */
  public subscribeToChanges(callback: (items: IGroupRequestChangeResponse[]) => void) {
    this.socket.on('change', (data) => {
      callback(data.items);
    });
  }

  /**
   * Unsubscribe from group changes
   * */
  public unSubscribeFromChanges() {
    this.socket.off(`change`);
  }

  /**
   * Getting groups
   *
   * @param page - number of requested page
   * @param perPage - per page, 0 for getting all
   * @param filter - filter, to be implemented
   *
   * @return Promise with {@link Group} array and {@link Pagination} class;
   * */
  public getGroups(page = 1, perPage = 50, filter: unknown = {}) {

    return new Promise<{
      items: Group[],
      pagination: Pagination
    }>((resolve, reject) => {
      this.socket.emit('get', {
        page, perPage, filter
      }, (response: ApiResponse) => {

        if (response.status) {
          // @TODO: remove when back fix
          const subI = response.result.items.map(item => {
            if (item.dynamicData?.lastMessage) {
              return {
                ...item,
                dynamicData: {
                  ...item.dynamicData,
                  lastMessage: {
                    ...item.dynamicData.lastMessage,
                    content: {
                      ...item.dynamicData.lastMessage.content,
                      attachments: item.dynamicData.lastMessage.content.attachments.filter(att => !!att)
                    }
                  }
                }
              }
            }

            return item;
          })
          const items = plainToInstance(Group, subI) as unknown as Group[];
          const pagination = plainToInstance(Pagination, response.result.pagination) as unknown as Pagination;

          resolve({items, pagination})
        } else {
          reject(response);
        }
      })
    });
  }

  /**
   * Getting tags for worker groups
   *
   * @param filter - filter, to be implemented
   *
   * @return Promise with {@link GroupsTag} array
   * */
  public getTags(filter: unknown = {}) {

    return new Promise<GroupsTag[]>((resolve, reject) => {
      this.socket.emit('getTags', {filter}, (response: ApiResponse) => {

        if (response.status) {
          resolve(plainToInstance(GroupsTag, response.result.items) as unknown as GroupsTag[])
        } else {
          reject(response);
        }
      })
    });
  }

  /**
   * Getting full data for group,<br>
   * Currently it will return only counters for tabs.
   *
   * @param groupId - group id
   *
   * @return Promise with {@link IGroupFullDataCounter}
   * */
  public getGroupFullData(groupId: string) {

    return new Promise<IGroupFullData>((resolve, reject) => {
      this.socket.emit('getFull', {groupId}, (response: ApiResponse) => {

        if (response.status) {
          resolve(response.result)
        } else {
          reject(response);
        }
      })
    });
  }

  /**
   * Getting full data for group,<br>
   * Currently it will return only counters for tabs.
   *
   * @param page - page number
   * @param perPage - per page, 0 to get all
   * @param filter - Filter with GroupId, and requested type {@link GroupContentItemType}
   *
   * @return Promise with {@link GroupContentItem} array and {@link Pagination} class
   * */
  public getGroupContent(page = 1, perPage = 50, filter: IGetGroupContentFilter = null) {
    return new Promise<{
      items: GroupContentItem[],
      pagination: Pagination
    }>((resolve, reject) => {
      this.socket.emit('getContent', {
        perPage,
        page,
        filter
      }, (response: ApiResponse) => {

        if (response.status) {
          const items = plainToInstance(GroupContentItem, response.result.items) as unknown as GroupContentItem[];
          const pagination = plainToInstance(Pagination, response.result.pagination) as unknown as Pagination;

          resolve({items, pagination})
        } else {
          reject(response);
        }
      })
    });
  }
}
